import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import colors from '@pickles/shared/utils/colors';

const countdownInterval = 500;

interface CountdownBarTimerProps {
  isActive: boolean;
  countdownDurationInS: number;
  className?: string;
}

export interface CountdownBarTimerHandles {
  resetTimer: () => void;
}

const CountdownBarTimer = forwardRef<CountdownBarTimerHandles, CountdownBarTimerProps>(
  ({ isActive, countdownDurationInS, className }, ref) => {
    const [timeLeft, setTimeLeft] = useState(countdownDurationInS * 1000);
    const [progress, setProgress] = useState(100);
    const progressRef = useRef(100);

    const resetTimer = () => {
      if (isActive) {
        setTimeLeft(countdownDurationInS * 1000);
        setProgress(100);
        progressRef.current = 100;
      }
    };

    useImperativeHandle(ref, () => ({
      resetTimer,
    }));

    useEffect(() => {
      let timer: NodeJS.Timeout;

      if (isActive && timeLeft > 0) {
        timer = setTimeout(() => {
          setTimeLeft((prevTime) => prevTime - countdownInterval);
        }, countdownInterval);
      }

      return () => clearTimeout(timer);
    }, [timeLeft, isActive, countdownDurationInS]);

    useEffect(() => {
      if (isActive) {
        const countdownDurationInMs = countdownDurationInS * 1000;
        const newProgress = (timeLeft / countdownDurationInMs) * 100;
        setProgress(Math.max(0, newProgress));
      }
    }, [timeLeft, isActive, countdownDurationInS]);

    return (
      <Container className={className}>
        {isActive && (
          <ProgressBar progress={progress} />
        )}
      </Container>
    );
  }
);

CountdownBarTimer.displayName = 'CountdownBarTimer';

export default CountdownBarTimer;

// Styled components
const Container = styled.div`
  width: 100%;
  height: 12px;
  border-radius: 6px;
  background-color: ${colors.picklesLightGrey};
  overflow: hidden;
  position: relative;
`;

const ProgressBar = styled.div<{ progress: number }>`
  height: 100%;
  width: 100%;
  border-radius: 6px;
  background-color: ${({ progress }) => {
    if (progress <= 30) return colors.picklesLiveRed;
    if (progress <= 70) return colors.orange;
    return colors.green;
  }};
  transform: scaleX(${({ progress }) => progress / 100});
  transform-origin: right;
  transition: transform ${countdownInterval}ms linear, background-color 0.3s ease;
`;
